// schema
import * as ISchema from "@schema";

// libraries
import { cloneDeep } from "lodash";

import {
  EventColorCategory,
  EventColorCategorySort,
  calendarColors,
  isValidCategory,
  filterAndSortCategories,
  getCategoryLabels,
  getColorFromSetting,
  getColor,
  getCurrentSettings,
  getEventColor,
  IEventCategoryColoring as IEventCategoryColoringSource,
  defaultSet,
} from "@clockwise/client-commons/src/util/event-category-coloring";

export {
  EventColorCategory,
  EventColorCategorySort,
  calendarColors,
  isValidCategory,
  filterAndSortCategories,
  getCategoryLabels,
  getColorFromSetting,
  getColor,
  getCurrentSettings,
  getEventColor,
};

export type IEventCategoryColoring = IEventCategoryColoringSource;

//////////////////
// FUNCTIONS
//////////////////

export function getExternalSetting(eventColoringSettings: ISchema.EventColoringSettingsErrorable) {
  if (eventColoringSettings.__typename === "GraphEntityError") {
    return undefined;
  }

  return getSettingByCategory(eventColoringSettings, EventColorCategory.External);
}

export function getOneOnOneSetting(eventColoringSettings: ISchema.EventColoringSettingsErrorable) {
  if (eventColoringSettings.__typename === "GraphEntityError") {
    return undefined;
  }
  return getSettingByCategory(eventColoringSettings, EventColorCategory.OneOnOne);
}

export function getSettingByCategory(
  eventColoringSettings: ISchema.IEventColoringSettings,
  category: EventColorCategory,
) {
  return eventColoringSettings.eventCategoryColorings.find((ecc) => ecc.eventCategory === category);
}

// merges remote settings, if any, with defaults
export function getMergedDefaultSet(
  eventColoringSettings?: ISchema.EventColoringSettingsErrorable,
) {
  return defaultSet.map((defaultSetting) => {
    let googleColorKey = defaultSetting.googleColorKey;
    let active = defaultSetting.active;
    if (!!eventColoringSettings && eventColoringSettings.__typename === "EventColoringSettings") {
      const remoteSetting = getSettingByCategory(
        eventColoringSettings,
        defaultSetting.eventCategory as EventColorCategory,
      );
      googleColorKey =
        (remoteSetting && remoteSetting.active && remoteSetting.googleColorKey) ||
        defaultSetting.googleColorKey;
      active =
        remoteSetting && remoteSetting.active !== undefined
          ? remoteSetting.active
          : defaultSetting.active;
    }
    const label = getCategoryLabels(defaultSetting.eventCategory).label;
    const color = calendarColors[googleColorKey].foreground;
    return { color, label, active };
  });
}

/**
 * Merges color settings with the defaults, returning an object that matches the schema settings, differing from above method.
 *
 * @param eventColorSettings existing eventColoringSettings
 * @param active whether to set colors to default active
 */
export function getMergedDefaultSettings(
  eventColorSettings?: ISchema.EventColoringSettingsErrorable,
  active = true,
): ISchema.IEventColoringSettings | undefined {
  if (!eventColorSettings || eventColorSettings.__typename === "GraphEntityError") {
    return;
  }

  const clonedEventColorSettings = cloneDeep(eventColorSettings);

  clonedEventColorSettings.eventCategoryColorings = filterAndSortCategories(
    clonedEventColorSettings,
  ).map((ecc) => {
    const maybeDefaultSetting = defaultSet.find((ds) => ds.eventCategory === ecc.eventCategory);

    return {
      ...ecc,
      googleColorKey:
        ecc.googleColorKey || (maybeDefaultSetting && maybeDefaultSetting.googleColorKey) || "",
      active,
    };
  });

  return clonedEventColorSettings;
}

export function resetEventColoringSettings(
  eventColoringSettings?: ISchema.EventColoringSettingsErrorable,
) {
  return filterAndSortCategories(eventColoringSettings).map((ecc) => {
    const maybeDefaultSetting = defaultSet.find((ds) => ds.eventCategory === ecc.eventCategory);
    return {
      eventCategory: ecc.eventCategory,
      active: ecc.active,
      googleColorKey: maybeDefaultSetting ? maybeDefaultSetting.googleColorKey : undefined,
      isUserSet: ecc.isUserSet,
    };
  });
}
